import { useEffect, useState } from 'react';
import getOrdinalSuffix from '~/utilities/flp/getOrdinalSuffix';

export type ExpirationInfo = {
  day: string | null;
  daysRemaining: number | null;
  isLoading: boolean;
  isValidExpiration: boolean;
  month: string | null;
  percentage: number | null;
  suffix: string | null;
};

const MAX_CREDIT_APP_EXPIRATION_DAYS = 30;

const EMPTY_EXPIRATION_INFO: ExpirationInfo = {
  day: null,
  daysRemaining: null,
  isLoading: true,
  isValidExpiration: false,
  month: null,
  percentage: null,
  suffix: null
};

export function computeExpirationInfo(
  expiration: string | null | undefined
): ExpirationInfo {
  const isValidExpiration = !isNaN(Date.parse(expiration ?? ''));

  if (!isValidExpiration) {
    return { ...EMPTY_EXPIRATION_INFO, isLoading: false };
  }

  const expirationDate = new Date(expiration as string);
  const dayInMillis = 1000 * 60 * 60 * 24;
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const rawDaysRemaining = Math.floor(
    (expirationDate.getTime() - todayStart) / dayInMillis
  );
  // Expiring today (0) still shows as 1 day left; already expired (negative)
  // clamps to 0 so the timer hides instead of showing a phantom day.
  const daysRemaining =
    rawDaysRemaining < 0 ? 0 : Math.max(rawDaysRemaining, 1);

  return {
    day: expirationDate.getDate().toString(),
    daysRemaining,
    isLoading: false,
    isValidExpiration,
    month: expirationDate.toLocaleDateString('en-US', { month: 'long' }),
    percentage: daysRemaining / MAX_CREDIT_APP_EXPIRATION_DAYS,
    suffix: getOrdinalSuffix(expirationDate.getDate())
  };
}

// No Date math needed for an empty expiration, so it's safe to resolve synchronously.
export function getInitialExpirationInfo(
  expiration: string | null | undefined
): ExpirationInfo {
  return expiration
    ? EMPTY_EXPIRATION_INFO
    : { ...EMPTY_EXPIRATION_INFO, isLoading: false };
}

export default function useExpirationInfo(
  expiration: string | null | undefined
): ExpirationInfo {
  const [expirationInfo, setExpirationInfo] = useState<ExpirationInfo>(() =>
    getInitialExpirationInfo(expiration)
  );

  useEffect(() => {
    setExpirationInfo(computeExpirationInfo(expiration));
  }, [expiration]);

  return expirationInfo;
}
